import { z } from "zod";

import type { Coverage } from "@/src/models/coverage";
import type { Patient } from "@/src/models/patient";
import { stediEligibilityStatusSchema } from "@/src/models/stedi-eligibility-status";

export enum EligibilityCoveragePosition {
  PRIMARY = "primary",
  SECONDARY = "secondary",
}

export function getEligibilityCoveragePosition(coverage: Pick<Coverage, "order"> | null): EligibilityCoveragePosition {
  if (coverage?.order != null && coverage.order !== 1 && coverage.order !== 2) {
    throw new Error(`Unsupported eligibility coverage order: ${coverage.order}`);
  }

  return coverage?.order === 2 ? EligibilityCoveragePosition.SECONDARY : EligibilityCoveragePosition.PRIMARY;
}

export function getPatientEligibilityCoverages(patient: Pick<Patient, "coverages">) {
  const coverages = patient.coverages?.filter((coverage) => coverage.deletedAt === null) ?? [];

  return {
    primaryCoverage:
      coverages.find((coverage) => coverage.order === 1) ??
      coverages.find((coverage) => coverage.order == null) ??
      null,
    secondaryCoverage: coverages.find((coverage) => coverage.order === 2) ?? null,
  };
}

export const eligibilityCoveragePositionSchema = z.enum(EligibilityCoveragePosition);

export const eligibilityCoverageQuerySchema = z.object({
  coveragePosition: eligibilityCoveragePositionSchema.optional(),
});

export const eligibilityCoverageSelectionSchema = z
  .object({
    coverageId: z.string().uuid().optional(),
  })
  .strict();

export type EligibilityCoverage = Pick<Coverage, "order"> & { id: string | null };

export const stediEligibilityCheckSchema = z.object({
  coveragePosition: eligibilityCoveragePositionSchema.default(EligibilityCoveragePosition.PRIMARY),
  patientId: z.string().nullable(),
  referralId: z.string().nullable(),
  taskId: z.string().nullable(),
  requestJson: z.unknown().optional(),
  responseJson: z.unknown().optional(),
  officeVisitRequiresAuth: z.boolean().nullable(),
  officeVisitAuthReason: z.string().nullable(),
  checkFailed: z.boolean().nullable(),
  eligibilityStatus: stediEligibilityStatusSchema.nullable(),
  payerName: z.string().nullable(),
  planType: z.string().nullable(),
  planName: z.string().nullable(),
  inNetwork: z.boolean().nullable(),
  stediCheckId: z.string().nullable(),
  tradingPartnerServiceId: z.string().nullable(),
  benefitRelatedPayerIds: z.array(z.string().trim().min(1)).nullable().default(null),
  primaryErrorCode: z.string().nullable(),
  primaryErrorDescription: z.string().nullable(),
  pcpPractitionerId: z.string().nullable(),
  valerieInsurancePlanId: z.string().uuid().nullable().default(null),
});

export const storedStediEligibilityCheckSchema = stediEligibilityCheckSchema.extend({
  id: z.string(),
  groupId: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
});

export type StediEligibilityCheck = z.infer<typeof storedStediEligibilityCheckSchema>;

export const verifyInsuranceEligibilityResponseSchema = z.object({
  skipped: z.boolean(),
  reason: z.string().optional(),
  message: z.string().optional(),
  check: storedStediEligibilityCheckSchema.nullable(),
});

export type VerifyInsuranceEligibilityResponse = z.infer<typeof verifyInsuranceEligibilityResponseSchema>;

export const stediEligibilityCheckShape: StediEligibilityCheck = {
  id: "",
  groupId: "",
  createdAt: "",
  updatedAt: "",
  deletedAt: null,
  coveragePosition: EligibilityCoveragePosition.PRIMARY,
  patientId: null,
  referralId: null,
  taskId: null,
  requestJson: {},
  responseJson: {},
  officeVisitRequiresAuth: null,
  officeVisitAuthReason: null,
  checkFailed: null,
  eligibilityStatus: null,
  payerName: null,
  planType: null,
  planName: null,
  inNetwork: null,
  stediCheckId: null,
  tradingPartnerServiceId: null,
  benefitRelatedPayerIds: null,
  primaryErrorCode: null,
  primaryErrorDescription: null,
  pcpPractitionerId: null,
  valerieInsurancePlanId: null,
};
